import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { House, Clock, CheckCircle, Broadcast, Plus, ArrowsClockwise, X } from "@phosphor-icons/react";

/**
 * AdminTurnsBoard — office view of vacant turns.
 * Shows Open pool + In-progress + Awaiting-release; office can Release
 * finished turns and manually add turns from any source.
 */
export default function AdminTurnsBoard() {
  const [board, setBoard] = useState({ open: [], in_progress: [] });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [releasing, setReleasing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/turns/board");
      setBoard(r.data || { open: [], in_progress: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const release = async (id) => {
    setReleasing(id);
    try {
      await api.post(`/turns/${id}/release`);
      toast.success("Turn released · marked complete");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not release"));
    } finally {
      setReleasing(null);
    }
  };

  const awaiting = (board.in_progress || []).filter(j => j.turn_awaiting_release || j.turn_finished_at);
  const inProgActive = (board.in_progress || []).filter(j => !j.turn_awaiting_release && !j.turn_finished_at);

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="h-12 w-12 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-lg flex items-center justify-center border-2 border-slate-900 shadow-[3px_3px_0px_#0f172a]">
            <Broadcast weight="fill" size={26} className="text-white"/>
          </div>
          <div className="flex-1">
            <div className="font-display font-black text-3xl uppercase tracking-tight leading-none">Turns Board</div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-1">
              Live vacant-unit pool · self-serve for the 7 AM crew
            </div>
          </div>
          <button onClick={load} data-testid="turns-refresh-btn"
                  className="h-10 w-10 border-2 border-slate-900 bg-white flex items-center justify-center hover:bg-slate-100">
            <ArrowsClockwise size={18} weight="bold"/>
          </button>
          <button onClick={() => setAddOpen(true)} data-testid="turns-add-btn"
                  className="h-10 px-4 bg-cyan-500 hover:bg-cyan-600 text-white font-black uppercase tracking-widest text-xs border-2 border-slate-900 shadow-[3px_3px_0px_#0f172a] flex items-center gap-1">
            <Plus size={16} weight="bold"/> Add Turn
          </button>
        </div>

        {loading && (
          <div className="text-center py-12 text-sm font-black uppercase tracking-widest text-slate-500">
            Loading…
          </div>
        )}

        {/* 3-column layout on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Column: Open Pool */}
          <ColumnCard title="Open Pool" count={board.open?.length || 0} tone="cyan" testid="col-open">
            {(board.open || []).length === 0 && !loading && (
              <EmptyBlock label="No open turns"/>
            )}
            {(board.open || []).map(j => (
              <TurnCard key={j.id} j={j} testid={`admin-turn-open-${j.id}`}
                        actions={<Link to={`/admin/jobs/${j.id}`}
                                       className="h-9 px-3 border-2 border-slate-900 bg-white text-xs font-black uppercase tracking-widest flex items-center hover:bg-slate-100">
                          Open WO
                        </Link>}/>
            ))}
          </ColumnCard>

          {/* Column: In Progress (active) */}
          <ColumnCard title="In Progress" count={inProgActive.length} tone="fuchsia" testid="col-inprog">
            {inProgActive.length === 0 && !loading && <EmptyBlock label="No active crews"/>}
            {inProgActive.map(j => (
              <TurnCard key={j.id} j={j} testid={`admin-turn-inprog-${j.id}`}
                        subline={<span>Started by <b>{j.turn_started_by_name || "tech"}</b> · {j.turn_started_at ? new Date(j.turn_started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—"}</span>}
                        actions={<Link to={`/admin/jobs/${j.id}`}
                                       className="h-9 px-3 border-2 border-slate-900 bg-white text-xs font-black uppercase tracking-widest flex items-center hover:bg-slate-100">
                          Open WO
                        </Link>}/>
            ))}
          </ColumnCard>

          {/* Column: Awaiting Release */}
          <ColumnCard title="Awaiting Release" count={awaiting.length} tone="emerald" testid="col-awaiting">
            {awaiting.length === 0 && !loading && <EmptyBlock label="Nothing awaiting release"/>}
            {awaiting.map(j => (
              <TurnCard key={j.id} j={j} testid={`admin-turn-awaiting-${j.id}`}
                        subline={<span>Finished by <b>{j.turn_started_by_name || "tech"}</b> · {j.turn_finished_at ? new Date(j.turn_finished_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—"}</span>}
                        actions={
                          <>
                            <Link to={`/admin/jobs/${j.id}`}
                                  className="h-9 px-3 border-2 border-slate-900 bg-white text-xs font-black uppercase tracking-widest flex items-center hover:bg-slate-100">
                              Open WO
                            </Link>
                            <button
                              data-testid={`turns-release-btn-${j.id}`}
                              onClick={() => release(j.id)}
                              disabled={releasing === j.id}
                              className="h-9 px-3 border-2 border-slate-900 bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-xs font-black uppercase tracking-widest flex items-center gap-1 shadow-[2px_2px_0px_#0f172a] disabled:opacity-50">
                              <CheckCircle weight="fill" size={14}/>
                              {releasing === j.id ? "…" : "Release"}
                            </button>
                          </>
                        }/>
            ))}
          </ColumnCard>
        </div>

        {addOpen && <AddTurnModal onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }}/>}
      </div>
    </AdminShell>
  );
}

function ColumnCard({ title, count, tone, testid, children }) {
  const toneClass = {
    cyan: "border-cyan-500 bg-cyan-50 text-cyan-800",
    fuchsia: "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800",
    emerald: "border-emerald-500 bg-emerald-50 text-emerald-800",
  }[tone] || "border-slate-500 bg-slate-50 text-slate-800";
  return (
    <div className="bg-white border-2 border-slate-900 rounded-lg overflow-hidden shadow-[3px_3px_0px_#0f172a]" data-testid={testid}>
      <div className={`px-4 py-2 border-b-2 border-slate-900 flex items-center justify-between ${toneClass}`}>
        <div className="font-display font-black text-sm uppercase tracking-widest">{title}</div>
        <div className="text-xs font-black">{count}</div>
      </div>
      <div className="p-3 space-y-2 max-h-[70vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

function TurnCard({ j, actions, subline, testid }) {
  return (
    <div className="bg-slate-50 border border-slate-300 rounded p-3" data-testid={testid}>
      <div className="flex items-start gap-2">
        <House weight="fill" size={16} className="text-slate-500 mt-0.5 flex-shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black text-slate-900 truncate">{j.property_address || "—"}</div>
          <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{j.title}</div>
          {subline && <div className="text-[10px] font-bold text-slate-500 mt-1">{subline}</div>}
          <div className="flex gap-1 mt-1 flex-wrap">
            <span className="text-[9px] font-black uppercase tracking-widest bg-white border border-slate-300 px-1.5 py-0.5 rounded text-slate-600">
              {j.source}
            </span>
            {j.vendor_name && (
              <span className="text-[9px] font-black uppercase tracking-widest bg-fuchsia-100 px-1.5 py-0.5 rounded text-fuchsia-700">
                {j.vendor_name}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 flex gap-1.5 flex-wrap">
        {actions}
      </div>
    </div>
  );
}

function EmptyBlock({ label }) {
  return (
    <div className="text-center py-6 text-xs font-black uppercase tracking-widest text-slate-400">
      {label}
    </div>
  );
}

function AddTurnModal({ onClose, onCreated }) {
  const [address, setAddress] = useState("");
  const [unit, setUnit] = useState("");
  const [title, setTitle] = useState("Turn / Vacant unit");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (address.trim().length < 3) { toast.error("Address required"); return; }
    setSaving(true);
    try {
      await api.post("/turns/manual", {
        property_address: address.trim(),
        unit: unit.trim() || null,
        title: title.trim() || "Turn / Vacant unit",
      });
      toast.success("Added to the pool");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not add"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
         data-testid="turns-add-modal">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl">
        <div className="p-4 border-b-2 border-cyan-500 flex items-center gap-2 bg-cyan-50">
          <Broadcast weight="fill" size={22} className="text-cyan-700"/>
          <div className="font-display font-black text-lg uppercase tracking-tight flex-1 text-cyan-900">
            Add Turn to Pool
          </div>
          <button onClick={onClose} data-testid="turns-add-close" className="text-slate-500 hover:text-slate-800 h-9 w-9 flex items-center justify-center"><X size={20}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5">Property Address</div>
            <input type="text" data-testid="turns-add-address" value={address} onChange={e => setAddress(e.target.value)}
                   placeholder="123 Main St" className="w-full h-11 px-3 border-2 border-slate-300 rounded-lg font-medium text-sm focus:border-cyan-500 focus:outline-none"/>
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5">Unit · optional</div>
            <input type="text" data-testid="turns-add-unit" value={unit} onChange={e => setUnit(e.target.value)}
                   placeholder="2B" className="w-full h-11 px-3 border-2 border-slate-300 rounded-lg font-medium text-sm focus:border-cyan-500 focus:outline-none"/>
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5">Title</div>
            <input type="text" data-testid="turns-add-title" value={title} onChange={e => setTitle(e.target.value)}
                   className="w-full h-11 px-3 border-2 border-slate-300 rounded-lg font-medium text-sm focus:border-cyan-500 focus:outline-none"/>
          </div>
        </div>
        <div className="p-4 border-t-2 border-slate-200 bg-slate-50">
          <button data-testid="turns-add-submit" onClick={submit} disabled={saving}
                  className="w-full h-12 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white font-display font-black uppercase tracking-widest text-sm rounded-lg border-2 border-slate-900 shadow-[3px_3px_0px_#0f172a]">
            {saving ? "Adding…" : "Add to Pool"}
          </button>
        </div>
      </div>
    </div>
  );
}
